import React, { FC, useState } from 'react';

import { faHeart } from '@fortawesome/pro-light-svg-icons/faHeart';
import { faStar, faHeart as heartFilled } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'epn-ui/umd/dist/components/Button';
import * as Sentry from '@sentry/nextjs';
import notification from 'epn-ui/umd/dist/components/Notification';
import Loader from 'epn-ui/umd/dist/components/Loader';
import clsx from 'clsx';
import useI18n, { LocaleTypes } from '@i18n';

import getPluralForm from '@utils/formatters/getPluralForm';
import routes from '@config/routes.json';
import BaseSemanticLink from '@base/BaseSemanticLink';
import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from '@utils/metrics/amplitude';
import sendGaEvent from '@utils/metrics/ga';
import api from '@api';
import OfferButtonGroup from '@pages/offers/Offer/OfferButtonGroup';
import styles from './index.module.scss';

export interface IOfferDataProp {
    id: number;
    cookieLive: string;
    cr: number;
    ecpa: string;
    confirm: number;
    isExclusive: boolean;
    image: string;
    name: string;
    rating: number;
    labelIds: number[];
    title: string;
    typeId: number;
    webmasterMaxRate: number | null;
    webmasterMaxRateSymbol: string | null;
    webmasterConfirmTimeInDays: string;
    url: string;
    status?: null | 'allow' | 'review' | 'waiting' | 'ban' | 'recheck';
}

export interface IOfferCardProps {
    offer: IOfferDataProp;
    onConnect: () => void;
    showTheReason: () => void;
    connection: {
        id?: string;
        offerId?: string;
        status?: 'allow' | 'ban' | 'review' | 'recheck' | 'waiting' | null;
        comment?: string | null;
    };
    mutateOffers: () => void;
    isAuth: boolean;
}

const daysLocaleMap = {
    [LocaleTypes.en]: ['day', 'days', 'days'],
    [LocaleTypes.ru]: ['день', 'дня', 'дней'],
};

const FAVORITE_LABEL_ID = 1;

const Offer: FC<IOfferCardProps> = ({
    offer: {
        id,
        cr,
        confirm,
        image,
        name,
        rating,
        labelIds,
        title,
        webmasterMaxRate,
        webmasterMaxRateSymbol,
        webmasterConfirmTimeInDays,
        url,
        status = null,
    },
    onConnect,
    showTheReason,
    connection,
    mutateOffers,
    isAuth,
}) => {
    const { t, locale } = useI18n('Offers');
    const { t: tCommon } = useI18n('Common');
    const [isFavoriteOffer, setIsFavoriteOffer] = useState(
        Boolean(labelIds?.includes(FAVORITE_LABEL_ID)),
    );
    const [isFavoriteOfferLoading, setIsFavoriteOfferLoading] = useState(false);

    const offerName = title || name;

    const setAsFavorites = () => {
        setIsFavoriteOfferLoading(true);
        try {
            (api.offer.setOfferAsFavorites({
                urlParams: { id },
            }) as Promise<Response>)
                .then(() => setIsFavoriteOffer(true))
                .finally(() => {
                    setIsFavoriteOfferLoading(false);
                    mutateOffers();
                });
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);
            notification.error({ message: t('Error while exporting. Try again later') });
        }
    };

    const deleteAsFavorites = async () => {
        setIsFavoriteOfferLoading(true);
        try {
            (api.offer.deleteOfferAsFavorites({
                urlParams: { id },
            }) as Promise<Response>)
                .then(() => setIsFavoriteOffer(false))
                .finally(() => {
                    setIsFavoriteOfferLoading(false);
                    mutateOffers();
                });
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);
            notification.error({ message: t('Error while exporting. Try again later') });
        }
    };

    const onOfferClick = () => {
        logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offersGoToOfferLogo, {
            offer_id: id,
        });

        sendGaEvent({
            eventCategory: 'offer',
            eventAction: 'offer_page_view',
            eventLabel: 'offer_id',
            eventValue: id,
        });
    };

    return (
        <Loader loading={isFavoriteOfferLoading}>
            <div className="flex flex-col border hover:border-blueGray-200 p-[8px] text-blueGray-600 xs:p-[16px] sm:p-[16px] lg:p-[24px] rounded-3 border-blueGray-100 h-[100%]">
                <div className="flex items-center justify-between">
                    <div className="text-p3">
                        <FontAwesomeIcon className="text-yellow-400" icon={faStar} />
                        <span className="ml-1 text-blueGray-500">{rating}</span>
                    </div>
                    {isAuth && isFavoriteOffer && (
                        <Button
                            type="default"
                            className="p-0 border-0 h-[16px]"
                            onClick={deleteAsFavorites}
                        >
                            <FontAwesomeIcon
                                className="text-red-400 cursor-pointer"
                                icon={heartFilled}
                            />
                        </Button>
                    )}
                    {isAuth && !isFavoriteOffer && (
                        <Button
                            type="default"
                            className="p-0 border-0 h-[16px]"
                            onClick={setAsFavorites}
                        >
                            <FontAwesomeIcon
                                className="text-red-400 cursor-pointer"
                                icon={faHeart}
                            />
                        </Button>
                    )}
                </div>
                <BaseSemanticLink onClick={onOfferClick} to={routes.offer.replace(':id', url)}>
                    <img
                        className="w-full p-0 mx-auto border-0 mb-[24px] max-w-[226px]"
                        src={image}
                        alt={t('affiliate program', { name: offerName })}
                        title={t('affiliate program', { name: offerName })}
                    />
                </BaseSemanticLink>

                <p className={styles.tooltip}>
                    <BaseSemanticLink
                        onClick={onOfferClick}
                        to={routes.offer.replace(':id', url)}
                        style={{ marginBlockEnd: '0', marginBlockStart: 'auto' }}
                        className="w-full mt-2 font-medium cursor-pointer text-wrap min-h-[50px] line-clamp-2 text-blueGray-800"
                    >
                        {offerName}
                    </BaseSemanticLink>
                    <span className={styles.tooltip_text}>{offerName}</span>
                </p>
                <div
                    className={clsx(
                        'flex flex-wrap justify-between mt-4 gap-y-[8px]',
                        (status === 'ban' || status === 'review' || status === 'waiting') &&
                            'opacity-50',
                    )}
                >
                    <div className="w-1/2 sm:w-[45%]">
                        <span className="text-p2 text-blueGray-300">{tCommon('Rate')}</span>
                        <p className="lowercase">{`${t('Up to')} ${webmasterMaxRate || 0}${
                            webmasterMaxRateSymbol || ''
                        }`}</p>
                    </div>
                    <div className="w-1/2 sm:w-[55%]">
                        <p className="overflow-hidden whitespace-nowrap  overflow-ellipsis text-p2 text-blueGray-300">
                            {t('The confirmation')}
                        </p>
                        <p>{confirm}%</p>
                    </div>
                    <div className="w-1/2 sm:w-[45%]">
                        <span className="text-p2 text-blueGray-300">{t('Average conversion')}</span>
                        <p>{cr}%</p>
                    </div>
                    <div className="w-1/2 sm:w-[55%]">
                        <span className="text-p2 text-blueGray-300">{t('Payment term')}</span>
                        <p>
                            ~{webmasterConfirmTimeInDays}{' '}
                            {getPluralForm(+webmasterConfirmTimeInDays, daysLocaleMap[locale])}
                        </p>
                    </div>
                </div>
                <OfferButtonGroup
                    offerId={id}
                    offerUrl={url}
                    onConnect={onConnect}
                    showTheReason={showTheReason}
                    connection={connection}
                    isAuth={isAuth}
                />
            </div>
        </Loader>
    );
};

export default Offer;
